import React, { useEffect, useState, useCallback } from "react";
import { Card, Row, Col, Spinner, Badge, Table } from "react-bootstrap";
import { useParams, useNavigate, useLocation, useOutletContext } from "react-router-dom";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import Header from "../components/Header";
import { getHost } from "../utils/utils";
import { FiArrowLeft } from "react-icons/fi";

const API_BASE = `${getHost()}/api/metrics`;
const getToken = () => localStorage.getItem("jwt");

const TIME_RANGES = [
  { label: "1h", hours: 1 },
  { label: "6h", hours: 6 },
  { label: "24h", hours: 24 },
  { label: "7d", hours: 168 },
];

const CHART_COLORS = {
  errorRate: "#FF4444",
  categories: "#7C98FF",
  search: "#00E98E",
  products: "#FFC107",
  bandwidth: "#7C98FF",
  requests: "#00E98E",
};

function formatPercent(rate) {
  return `${(rate * 100).toFixed(1)}%`;
}

function formatBytes(bytes) {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(2)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(2)} MB`;
  return `${(bytes / 1e3).toFixed(2)} KB`;
}

function formatTime(ts) {
  if (!ts) return "";
  const date = new Date(ts);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDatetime(ts) {
  if (!ts) return "N/A";
  const date = new Date(ts);
  return date.toLocaleDateString([], { month: "short", day: "numeric" }) +
    ", " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function getStatusColor(errorRate) {
  if (errorRate >= 0.3) return "#FF4444";
  if (errorRate >= 0.1) return "#FFC107";
  return "#00E98E";
}

const ChartTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-dark border border-secondary rounded p-2">
      <p className="text-white-65 fs-11 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="mb-0 fs-12" style={{ color: entry.color }}>
          {entry.name}: {formatter ? formatter(entry.value) : entry.value}
        </p>
      ))}
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <Col md={3} sm={6} xs={6} className="mb-3">
    <Card className="border border-dark bg-transparent h-100">
      <Card.Body className="p-3">
        <p className="text-white-65 fs-12 mb-1">{label}</p>
        <h5 className="fw-600 mb-0" style={{ color: color || "#ADADAD" }}>
          {value}
        </h5>
      </Card.Body>
    </Card>
  </Col>
);

const MonitorDetail = () => {
  const { toggleSidebar } = useOutletContext();
  const { monitorName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const healthPath = location.pathname.startsWith("/admin")
    ? "/admin/health"
    : "/monitors/health";
  const [metrics, setMetrics] = useState([]);
  const [rateLimit, setRateLimit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRange, setSelectedRange] = useState(24);
  const [expandedErrors, setExpandedErrors] = useState(new Set());

  const decodedName = decodeURIComponent(monitorName);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${getToken()}` };
      const [res, limitsRes] = await Promise.all([
        fetch(`${API_BASE}/${encodeURIComponent(decodedName)}?hours=${selectedRange}`, { headers }),
        fetch(`${getHost()}/api/admin/rate-limits`, { headers }).catch(() => null),
      ]);
      if (res.ok) {
        const data = await res.json();
        setMetrics(data.metrics || []);
      }
      if (limitsRes?.ok) {
        const limitsData = await limitsRes.json();
        const match = (limitsData.limits || []).find((l) => l.monitor === decodedName);
        setRateLimit(match || null);
      }
    } catch {
      // Keep existing state
    } finally {
      setLoading(false);
    }
  }, [decodedName, selectedRange]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const latest = metrics.length > 0 ? metrics[metrics.length - 1] : null;
  const rangeLabel = TIME_RANGES.find((r) => r.hours === selectedRange)?.label || "";

  // Aggregate cumulative counters across the selected time range
  const totals = metrics.reduce(
    (acc, m) => ({
      webhooks_sent: acc.webhooks_sent + (m.webhooks_sent || 0),
      products_discovered: acc.products_discovered + (m.products_discovered || 0),
      products_updated: acc.products_updated + (m.products_updated || 0),
      cf_solves: acc.cf_solves + (m.cf_solves || 0),
      imperva_solves: acc.imperva_solves + (m.imperva_solves || 0),
      total_requests:
        acc.total_requests +
        ((m.categories?.requests || 0) + (m.search?.requests || 0) + (m.products?.requests || 0)),
      total_bytes:
        acc.total_bytes +
        ((m.categories?.bytes_in || 0) + (m.search?.bytes_in || 0) + (m.products?.bytes_in || 0)),
    }),
    { webhooks_sent: 0, products_discovered: 0, products_updated: 0, cf_solves: 0, imperva_solves: 0, total_requests: 0, total_bytes: 0 }
  );

  const chartData = metrics.map((m) => ({
    time: formatTime(m.ts),
    error_rate: m.error_rate || 0,
    categories: (m.categories && m.categories.avg_duration_ms) || 0,
    search: (m.search && m.search.avg_duration_ms) || 0,
    products: (m.products && m.products.avg_duration_ms) || 0,
    bytes_in:
      ((m.categories && m.categories.bytes_in) || 0) +
      ((m.search && m.search.bytes_in) || 0) +
      ((m.products && m.products.bytes_in) || 0),
    requests:
      ((m.categories && m.categories.requests) || 0) +
      ((m.search && m.search.requests) || 0) +
      ((m.products && m.products.requests) || 0),
    cf_solves: m.cf_solves || 0,
    imperva_solves: m.imperva_solves || 0,
  }));

  // Aggregate errors across all data points
  const errorsMap = {};
  metrics.forEach((m) => {
    if (m.errors && Array.isArray(m.errors)) {
      m.errors.forEach((err) => {
        const key = err.pattern || err.message || "Unknown";
        if (!errorsMap[key]) {
          errorsMap[key] = { pattern: key, count: 0, last_seen: null, sample: err.sample || "" };
        }
        errorsMap[key].count += err.count || 1;
        const seen = err.last_seen || m.ts;
        if (!errorsMap[key].last_seen || new Date(seen) > new Date(errorsMap[key].last_seen)) {
          errorsMap[key].last_seen = seen;
          if (err.sample) errorsMap[key].sample = err.sample;
        }
      });
    }
  });
  const errorsList = Object.values(errorsMap).sort((a, b) => b.count - a.count);

  const pagesPerCategory = latest?.pages_per_category || {};
  const pagesPerKeyword = latest?.pages_per_keyword || {};
  const pagesEntries = [
    ...Object.entries(pagesPerCategory).map(([key, pages]) => ({ kind: "category", key, pages })),
    ...Object.entries(pagesPerKeyword).map(([key, pages]) => ({ kind: "keyword", key, pages })),
  ].sort((a, b) => b.pages - a.pages);

  const toggleError = (pattern) => {
    setExpandedErrors((prev) => {
      const next = new Set(prev);
      if (next.has(pattern)) next.delete(pattern);
      else next.add(pattern);
      return next;
    });
  };

  return (
    <div className="w-100 monitor-setting">
      <Header toggleSidebar={toggleSidebar} />
      <div className="pb-4 mx-4 content-body">
        <Row className="gx-0 align-items-center">
          <Col md={12}>
            <div className="mb-2">
              <button
                className="bg-transparent border-0 text-gray fs-13 mb-2 p-0 cursor-pointer d-flex align-items-center gap-1"
                onClick={() => navigate(healthPath)}
              >
                <FiArrowLeft /> Back to Health Overview
              </button>
              <div className="d-flex flex-wrap align-items-center gap-2 gap-md-3 mb-1">
                <h4 className="fs-22 text-main mb-0 text-break">{decodedName}</h4>
                <button
                  className="border-0 rounded px-2 py-1 fs-12 fw-500 cursor-pointer"
                  style={{ backgroundColor: "#7C98FF22", color: "#7C98FF" }}
                  onClick={() => navigate(`/admin/logs/${encodeURIComponent(decodedName)}`)}
                  title="View logs from Loki"
                >
                  Logs
                </button>
                {latest && (
                  <>
                    <Badge
                      bg=""
                      className="fs-11 fw-500"
                      style={{
                        backgroundColor: `${getStatusColor(latest.error_rate)}22`,
                        color: getStatusColor(latest.error_rate),
                      }}
                    >
                      {formatPercent(latest.error_rate)} error rate
                    </Badge>
                    {latest.proxy_type && (
                      <Badge
                        bg=""
                        className="fs-11 fw-500"
                        style={{ backgroundColor: "#7C98FF22", color: "#7C98FF" }}
                      >
                        {latest.proxy_type}
                      </Badge>
                    )}
                    {latest.restart_count > 0 && (
                      <Badge
                        bg=""
                        className="fs-11 fw-500"
                        style={{ backgroundColor: "#FFC10722", color: "#FFC107" }}
                      >
                        {latest.restart_count} restart{latest.restart_count !== 1 ? "s" : ""}
                      </Badge>
                    )}
                  </>
                )}
              </div>
              {latest && latest.started_at && (
                <p className="text-white-65 fs-12 mb-0">
                  Running since: {formatDatetime(latest.started_at)}
                </p>
              )}
            </div>
          </Col>
        </Row>
      </div>

      <div className="px-4 mb-3">
        <div className="d-flex gap-2">
          {TIME_RANGES.map((range) => (
            <button
              key={range.hours}
              className={`border rounded px-3 py-1 fs-12 fw-500 cursor-pointer ${
                selectedRange === range.hours
                  ? "bg-white-15 text-main"
                  : "bg-transparent text-white-65 border-dark"
              }`}
              style={{ borderColor: selectedRange === range.hours ? "#7C98FF" : undefined }}
              onClick={() => setSelectedRange(range.hours)}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 mb-4">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            {latest && (
              <>
                <Row className="mb-2">
                  <StatCard
                    label="Error Rate (current)"
                    value={formatPercent(latest.error_rate)}
                    color={getStatusColor(latest.error_rate)}
                  />
                  <StatCard
                    label="Avg Cycle Time (current)"
                    value={latest.avg_cycle_time != null ? `${latest.avg_cycle_time}s` : "N/A"}
                    color={latest.avg_cycle_time > 30 ? "#FFC107" : undefined}
                  />
                  <StatCard
                    label="Products Tracked (current)"
                    value={(latest.products_tracked || 0).toLocaleString()}
                    color="#00E98E"
                  />
                  <StatCard
                    label={`Webhooks Sent (${rangeLabel})`}
                    value={totals.webhooks_sent.toLocaleString()}
                    color="#7C98FF"
                  />
                </Row>
                <Row className="mb-4">
                  <StatCard
                    label={`Requests (${rangeLabel})`}
                    value={totals.total_requests.toLocaleString()}
                  />
                  <StatCard
                    label={`Bandwidth (${rangeLabel})`}
                    value={formatBytes(totals.total_bytes)}
                  />
                  {totals.cf_solves > 0 && (
                    <StatCard
                      label={`CF Solves (${rangeLabel})`}
                      value={totals.cf_solves.toLocaleString()}
                      color="#FFC107"
                    />
                  )}
                  {totals.imperva_solves > 0 && (
                    <StatCard
                      label={`Imperva Solves (${rangeLabel})`}
                      value={totals.imperva_solves.toLocaleString()}
                      color="#FFC107"
                    />
                  )}
                  {rateLimit && (() => {
                    const expired = !rateLimit.resets_at || rateLimit.resets_at < Date.now() / 1000;
                    const remaining = expired ? rateLimit.max : rateLimit.remaining;
                    const mins = expired ? 0 : Math.max(0, Math.ceil((rateLimit.resets_at - Date.now() / 1000) / 60));
                    return (
                      <StatCard
                        label={`Solves Left${mins > 0 ? ` · resets in ${mins}m` : ""}`}
                        value={`${remaining}/${rateLimit.max}`}
                        color={remaining === 0 ? "#FF4444" : "#00E98E"}
                      />
                    );
                  })()}
                </Row>
              </>
            )}

            {/* Error Rate Chart */}
            <Row className="mb-4">
              <Col lg={6} className="mb-3">
                <Card className="border border-dark bg-transparent h-100">
                  <Card.Body className="p-3">
                    <p className="text-white-65 fs-12 mb-3">Error Rate Over Time</p>
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                        <XAxis dataKey="time" stroke="#848484" style={{ fontSize: "10px" }} tick={{ fill: "#848484" }} />
                        <YAxis stroke="#848484" style={{ fontSize: "10px" }} tick={{ fill: "#848484" }} />
                        <Tooltip content={<ChartTooltip formatter={formatPercent} />} />
                        <Area
                          type="monotone"
                          dataKey="error_rate"
                          stroke={CHART_COLORS.errorRate}
                          fill={CHART_COLORS.errorRate}
                          fillOpacity={0.3}
                          strokeWidth={2}
                          name="Error Rate"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card>
              </Col>

              {/* Cycle Duration Chart */}
              <Col lg={6} className="mb-3">
                <Card className="border border-dark bg-transparent h-100">
                  <Card.Body className="p-3">
                    <p className="text-white-65 fs-12 mb-3">Cycle Duration by Task</p>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                        <XAxis dataKey="time" stroke="#848484" style={{ fontSize: "10px" }} tick={{ fill: "#848484" }} />
                        <YAxis stroke="#848484" style={{ fontSize: "10px" }} tick={{ fill: "#848484" }} />
                        <Tooltip content={<ChartTooltip />} />
                        <Legend
                          wrapperStyle={{ fontSize: "11px" }}
                          iconType="square"
                          iconSize={8}
                        />
                        <Bar dataKey="categories" stackId="a" fill={CHART_COLORS.categories} name="Categories" />
                        <Bar dataKey="search" stackId="a" fill={CHART_COLORS.search} name="Search" />
                        <Bar dataKey="products" stackId="a" fill={CHART_COLORS.products} name="Products" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="mb-4">
              {/* Bandwidth Chart */}
              <Col lg={6} className="mb-3">
                <Card className="border border-dark bg-transparent h-100">
                  <Card.Body className="p-3">
                    <p className="text-white-65 fs-12 mb-3">Bandwidth Over Time</p>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                        <XAxis dataKey="time" stroke="#848484" style={{ fontSize: "10px" }} tick={{ fill: "#848484" }} />
                        <YAxis stroke="#848484" style={{ fontSize: "10px" }} tick={{ fill: "#848484" }} tickFormatter={formatBytes} />
                        <Tooltip content={<ChartTooltip formatter={formatBytes} />} />
                        <Line
                          type="monotone"
                          dataKey="bytes_in"
                          stroke={CHART_COLORS.bandwidth}
                          strokeWidth={2}
                          dot={false}
                          name="Bytes In"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card>
              </Col>

              {/* Request Count Chart */}
              <Col lg={6} className="mb-3">
                <Card className="border border-dark bg-transparent h-100">
                  <Card.Body className="p-3">
                    <p className="text-white-65 fs-12 mb-3">Requests Over Time</p>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                        <XAxis dataKey="time" stroke="#848484" style={{ fontSize: "10px" }} tick={{ fill: "#848484" }} />
                        <YAxis stroke="#848484" style={{ fontSize: "10px" }} tick={{ fill: "#848484" }} />
                        <Tooltip content={<ChartTooltip />} />
                        <Line
                          type="monotone"
                          dataKey="requests"
                          stroke={CHART_COLORS.requests}
                          strokeWidth={2}
                          dot={false}
                          name="Requests"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Pagination Depth */}
            {pagesEntries.length > 0 && (
              <Card className="border border-dark bg-transparent mb-4">
                <Card.Body className="p-3">
                  <p className="text-white-65 fs-12 mb-3">
                    Pagination Depth <span className="text-white-50">({pagesEntries.length} {pagesEntries.length === 1 ? "key" : "keys"}, {pagesEntries.reduce((a, e) => a + e.pages, 0)} pages total)</span>
                  </p>
                  <div className="table-mobile-scroll">
                    <Table className="table custom-table table-bordered" hover>
                      <thead>
                        <tr>
                          <th className="fs-13 fw-500 border-bottom">Type</th>
                          <th className="fs-13 fw-500 border-bottom">Key</th>
                          <th className="fs-13 fw-500 border-bottom">Pages</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagesEntries.map(({ kind, key, pages }) => (
                          <tr key={`${kind}-${key}`}>
                            <td className="text-white-65 fs-12 py-2 text-capitalize">{kind}</td>
                            <td className="text-main fs-12 py-2">{key}</td>
                            <td className="fs-12 py-2" style={{ color: pages > 20 ? "#FFC107" : "#00E98E" }}>
                              {pages}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Errors Table */}
            {errorsList.length > 0 && (
              <Card className="border border-dark bg-transparent">
                <Card.Body className="p-3">
                  <p className="text-white-65 fs-12 mb-3">Error Patterns</p>
                  <div className="table-mobile-scroll">
                  <Table className="table custom-table table-bordered" hover>
                    <thead>
                      <tr>
                        <th className="fs-13 fw-500 border-bottom">Pattern</th>
                        <th className="fs-13 fw-500 border-bottom">Count</th>
                        <th className="fs-13 fw-500 border-bottom">Last Seen</th>
                        <th className="fs-13 fw-500 border-bottom"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {errorsList.map((err) => (
                        <React.Fragment key={err.pattern}>
                          <tr
                            className="cursor-pointer"
                            onClick={() => toggleError(err.pattern)}
                          >
                            <td className="text-main fs-12 py-2">{err.pattern}</td>
                            <td style={{ color: "#FF4444" }} className="fs-12 py-2">
                              {err.count}
                            </td>
                            <td className="text-white-65 fs-12 py-2">
                              {err.last_seen ? new Date(err.last_seen).toLocaleString() : "N/A"}
                            </td>
                            <td className="text-white-65 fs-12 py-2">
                              {err.sample ? (expandedErrors.has(err.pattern) ? "Hide" : "Show") : ""}
                            </td>
                          </tr>
                          {expandedErrors.has(err.pattern) && err.sample && (
                            <tr>
                              <td colSpan={4} className="py-2 px-3">
                                <pre
                                  className="text-white-65 fs-11 mb-0 p-2 rounded"
                                  style={{
                                    backgroundColor: "#0C0C0C",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-all",
                                  }}
                                >
                                  {err.sample}
                                </pre>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </Table>
                  </div>
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MonitorDetail;
